import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Lock, CheckCircle } from 'lucide-react';
import { errorMessage } from '@/lib/errors';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError(t('resetPassword.minLength'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('resetPassword.mismatch'));
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        setError(errorMessage(error));
      } else {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 3000);
      }
    } catch {
      setError(t('resetPassword.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0a0a0a] to-[#111111] p-6 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#B8860B]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl" />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center">
            <span className="text-3xl font-bold tracking-tight">
              <span className="text-[#B8860B]">Beauty</span>
              <span className="text-[#D4AF37]">Book</span>
              <span className="text-[#B8860B] text-2xl">AI</span>
            </span>
          </Link>
        </div>

        <div className="w-full space-y-6 p-8 rounded-2xl bg-[#1a1a1a]/80 backdrop-blur-sm border border-[#B8860B]/20 shadow-2xl">
          {success ? (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-white">{t('resetPassword.successTitle')}</h1>
              <p className="text-gray-400">{t('resetPassword.successDescription')}</p>
              <Link
                to="/login"
                className="inline-block text-sm text-[#B8860B] hover:text-[#D4AF37] hover:underline"
              >
                {t('resetPassword.goToLogin')}
              </Link>
            </div>
          ) : (
            <>
              <div className="space-y-2 text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 rounded-full bg-[#B8860B]/10 flex items-center justify-center">
                    <Lock className="w-7 h-7 text-[#B8860B]" />
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-white">{t('resetPassword.title')}</h1>
                <p className="text-gray-400 text-sm">{t('resetPassword.subtitle')}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-300">{t('resetPassword.newPassword')}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    required
                    minLength={8}
                    className="bg-[#0a0a0a] border-gray-700 text-white placeholder:text-gray-500 focus:border-[#B8860B] focus:ring-[#B8860B]/20"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-gray-300">{t('resetPassword.confirmPassword')}</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    required
                    minLength={8}
                    className="bg-[#0a0a0a] border-gray-700 text-white placeholder:text-gray-500 focus:border-[#B8860B] focus:ring-[#B8860B]/20"
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-400">{error}</p>
                )}

                <Button
                  type="submit"
                  className="w-full bg-[#B8860B] hover:bg-[#D4AF37] text-black font-semibold transition-all duration-300"
                  disabled={loading || !password || !confirmPassword}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('resetPassword.updating')}
                    </>
                  ) : (
                    t('resetPassword.updatePassword')
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
